import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button, buttonVariants } from '@/components/ui/button'

import useCourses from '@/hooks/courses/useCourses'
import useCopyCourseDocuments from '@/hooks/documents/useCopyCourseDocuments'

export default function CopyDocumentsDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const [sourceCourse, setSourceCourse] = useState('')
  const [targetCourse, setTargetCourse] = useState('')

  const { data: coursesList } = useCourses()
  const { mutateAsync, isPending } = useCopyCourseDocuments()

  const courseOptions = useMemo(() => {
    return (coursesList ?? []).map((course) => course.courseName)
  }, [coursesList])

  const handleCopy = async () => {
    if (!sourceCourse || !targetCourse) {
      toast.error('Select both source and target courses')
      return
    }
    if (sourceCourse === targetCourse) {
      toast.error('Source and target courses must be different')
      return
    }

    await mutateAsync({ sourceCourse, targetCourse })
    setIsOpen(false)
    setSourceCourse('')
    setTargetCourse('')
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger className={`${buttonVariants({ size: 'sm' })} ml-auto h-8 lg:flex`}>
        <Copy className="mr-2 h-4 w-4" />
        Copy Documents
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy Documents</DialogTitle>
          <DialogDescription>
            Copy all documents from one course to another.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Source Course</span>
            <Select value={sourceCourse} onValueChange={setSourceCourse}>
              <SelectTrigger>
                <SelectValue placeholder="Select source course" />
              </SelectTrigger>
              <SelectContent side="bottom" className="max-h-[200px]">
                {courseOptions.map((course) => (
                  <SelectItem key={course} value={course} className="dark:focus:text-white">
                    {course}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Target Course</span>
            <Select value={targetCourse} onValueChange={setTargetCourse}>
              <SelectTrigger>
                <SelectValue placeholder="Select target course" />
              </SelectTrigger>
              <SelectContent side="bottom" className="max-h-[200px]">
                {courseOptions.map((course) => (
                  <SelectItem key={course} value={course} className="dark:focus:text-white">
                    {course}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button onClick={handleCopy} disabled={isPending}>
            {isPending ? 'Copying...' : 'Copy All'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
